import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Zap, 
  CheckCircle, 
  Building2,
  Banknote,
  Landmark,
  ArrowRight,
  X,
  Fingerprint,
  MousePointer2,
  Brain
} from 'lucide-react';
import './SafePulseInteractive.css';

const SafePulseInteractive = () => {
  const navigate = useNavigate();
  const [activeComparison, setActiveComparison] = useState('traditional');
  const [hoveredMetric, setHoveredMetric] = useState(null);

  const metrics = [
    {
      icon: Shield,
      label: 'Security Posture',
      traditional: 'Reactive',
      safepulse: 'Proactive',
      improvement: 'Continuous monitoring',
      color: '#0071e3'
    },
    {
      icon: Zap,
      label: 'Deployment Time',
      traditional: '6-12 months',
      safepulse: '2 weeks',
      improvement: '20x faster rollout',
      color: '#22c55e'
    },
    {
      icon: Lock,
      label: 'User Adoption',
      traditional: '40-60%',
      safepulse: '100%',
      improvement: 'Zero training needed',
      color: '#8b5cf6'
    },
    {
      icon: CheckCircle,
      label: 'Total Cost of Ownership',
      traditional: '$2.5M/yr',
      safepulse: '$950K/yr',
      improvement: '62% savings',
      color: '#f59e0b'
    }
  ];

  const features = [
    {
      icon: Building2,
      title: 'Rapid Deployment',
      description: 'Deploy across your entire organization in weeks, not months, with zero user training',
      metric: '2 weeks',
      metricLabel: 'Average rollout'
    },
    {
      icon: Zap,
      title: 'Instant ROI',
      description: 'See measurable cost savings within the first quarter from reduced fraud and IT overhead',
      metric: '90 days',
      metricLabel: 'To positive ROI'
    },
    {
      icon: Brain,
      title: 'Zero Change Management',
      description: 'Works invisibly in the background - users never notice the security layer',
      metric: '0 hours',
      metricLabel: 'User training'
    },
    {
      icon: CheckCircle,
      title: 'Compliance Ready',
      description: 'Pre-configured for NIST, GDPR, SOC 2, and industry-specific regulations',
      metric: '100%',
      metricLabel: 'Audit-ready'
    },
    {
      icon: Lock,
      title: 'Risk Reduction',
      description: 'Eliminate 90%+ of credential-based attacks with continuous verification',
      metric: '92%',
      metricLabel: 'Attack prevention'
    },
    {
      icon: Banknote,
      title: 'Cost Transformation',
      description: 'Turn security from a cost center into a business enabler with measurable savings',
      metric: '62%',
      metricLabel: 'TCO reduction'
    }
  ];

  return (
    <section className="sp-interactive-section">
      <div className="sp-interactive-container">
        {/* Header */}
        <motion.div 
          className="sp-interactive-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="header-badge">
            <Shield size={16} />
            <span>The Difference</span>
          </div>
          <h2>Why Organizations Choose SafePulse</h2>
          <p>Transform your security from a cost center to a business enabler with measurable impact across operations, compliance, and user experience.</p>
        </motion.div>

        {/* Comparison Toggle */}
        <div className="comparison-toggle">
          <button
            className={`toggle-btn ${activeComparison === 'traditional' ? 'active' : ''}`}
            onClick={() => setActiveComparison('traditional')}
          >
            {activeComparison === 'traditional' && (
              <motion.div 
                className="toggle-active-bg"
                layoutId="toggleActive"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="toggle-text">Traditional Security</span>
          </button>
          <button
            className={`toggle-btn ${activeComparison === 'safepulse' ? 'active' : ''}`}
            onClick={() => setActiveComparison('safepulse')}
          >
            {activeComparison === 'safepulse' && (
              <motion.div 
                className="toggle-active-bg"
                layoutId="toggleActive"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="toggle-text">SafePulse</span>
          </button>
        </div>

        {/* Metrics Comparison */}
        <div className="metrics-comparison">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            const showComparison = activeComparison === 'safepulse';
            
            return (
              <motion.div
                key={index}
                className="metric-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredMetric(index)}
                onMouseLeave={() => setHoveredMetric(null)}
              >
                <div className="metric-icon" style={{ backgroundColor: `${metric.color}15`, color: metric.color }}>
                  <Icon size={24} />
                </div>
                <div className="metric-content">
                  <div className="metric-label">{metric.label}</div>
                  {showComparison ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="metric-values">
                        <div className="metric-value traditional">
                          <span className="value-label">Before</span>
                          <span className="value-number">{metric.traditional}</span>
                        </div>
                        <ArrowRight size={20} className="metric-arrow" />
                        <div className="metric-value safepulse">
                          <span className="value-label">After</span>
                          <span className="value-number" style={{ color: metric.color }}>{metric.safepulse}</span>
                        </div>
                      </div>
                      <div className="metric-improvement" style={{ color: metric.color }}>
                        {metric.improvement}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div 
                      className="metric-single-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="value-number-large">{metric.traditional}</span>
                    </motion.div>
                  )}
                </div>
                {hoveredMetric === index && (
                  <motion.div 
                    className="metric-highlight"
                    layoutId="metricHighlight"
                    style={{ borderColor: metric.color }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Technology Features */}
        <motion.div 
          className="technology-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3>Business Impact</h3>
          <p className="tech-subtitle">Measurable outcomes that matter to your organization</p>
          
          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <div className="feature-icon-wrapper">
                    <Icon size={28} />
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                  <div className="feature-metric">
                    <span className="metric-number">{feature.metric}</span>
                    <span className="metric-text">{feature.metricLabel}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="cta-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="cta-content">
            <div className="cta-text">
              <h3>Experience the difference</h3>
              <p>See how SafePulse can transform your security posture while improving user experience</p>
            </div>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={() => navigate('/contact')}>
                <span>Request Demo</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SafePulseInteractive;
